import { DialogFooter, IDialogContentProps } from '@fluentui/react';
import { useContext, useState } from 'react';

import AppContext from 'App/AppContext';
import { IPDefaultButton, IPDialog, IPPrimaryButton } from 'baseComponents';
import { IPDialogType } from 'baseComponents/Dialog/IPDialog';
import WarningMessage from 'components/Notification/WarningMessage';
import { useTranslation } from 'react-i18next';
import { FeatureTypes, hasUserFeature } from 'services/Auth/featurePermissions';
import ConfirmActionContent from './ConfirmActionContent';
import { isConfirmActionTextValid } from './isConfirmActionTextValid';
import { IPStack } from 'baseComponents/Stack/IPStack';

export interface IDialogConfirmGenericProps {
  onYes: () => void;
  onNo: () => void;
  hidden: boolean;
  title: string;
  subText: string;
  confirmText?: string;
  optionalJSX?: JSX.Element;
  optionalJSXHeight?: string | number;
  optionalJSXWidth?: string | number;
  featureType?: FeatureTypes;
  hideWarning?: boolean;
  warning?: JSX.Element | null;
  // Skip the typed-confirmation text field for low-risk, reversible actions.
  // Yes/No is the only gate when this is set.
  hideConfirmInput?: boolean;
}

const DialogConfirmGeneric = (props: IDialogConfirmGenericProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation']);
  const defConfirmText = t('translation:Dialogs.ConfirmGeneric.DefaultConfirmDeleteText');
  const confirmText = props.confirmText ?? defConfirmText;

  const [enteredConfirmText, setEnteredConfirmText] = useState<string>(
    hasUserFeature(appContext, FeatureTypes.TenantAdmin) ? confirmText : '',
  );

  const dialogContentProps: IDialogContentProps = {
    type: IPDialogType.normal,
    title: props.title,
    showCloseButton: true,
    onDismiss: () => dismiss(),
  };

  const hasFeature = (): boolean => {
    if (!props.featureType) return true;

    if (!appContext.user.login.userLicensed || !hasUserFeature(appContext, props.featureType)) {
      return false;
    }

    return true;
  };

  const confirm = () => {
    setEnteredConfirmText('');
    props.onYes();
  };

  const dismiss = () => {
    setEnteredConfirmText('');
    props.onNo();
  };

  if (!hasFeature()) {
    return (
      <IPDialog hidden={props.hidden} onDismiss={props.onNo} dialogContentProps={dialogContentProps}>
        <IPStack verticalFill>
          <IPStack.Item>
            <WarningMessage message={t('translation:Dialogs.ConfirmGeneric.NoRole')} />
          </IPStack.Item>
        </IPStack>
        <DialogFooter>
          <IPDefaultButton onClick={() => dismiss()} text={t('translation:Dialogs.Ok.Ok')} />
        </DialogFooter>
      </IPDialog>
    );
  }

  return (
    <IPDialog hidden={props.hidden} dialogContentProps={dialogContentProps}>
      <ConfirmActionContent
        subText={props.subText}
        confirmText={confirmText}
        enteredConfirmText={enteredConfirmText}
        onEnteredConfirmTextChange={setEnteredConfirmText}
        optionalJSX={props.optionalJSX}
        optionalJSXHeight={props.optionalJSXHeight}
        optionalJSXWidth={props.optionalJSXWidth}
        hideWarning={props.hideWarning}
        warning={props.warning}
        hideConfirmInput={props.hideConfirmInput}
      />
      <DialogFooter>
        <IPPrimaryButton
          disabled={!isConfirmActionTextValid(enteredConfirmText, confirmText, props.hideConfirmInput)}
          onClick={() => confirm()}
          text={t('translation:Dialogs.YesNo.Yes')}
        />
        <IPDefaultButton onClick={() => dismiss()} text={t('translation:Dialogs.YesNo.No')} />
      </DialogFooter>
    </IPDialog>
  );
};

export default DialogConfirmGeneric;
